/**
 * DatasetFacetsContext
 *
 * Pages (Home, Katalog) populate this context with the set of industry/category IDs
 * present in their dataset. Right panel slots consume it to scope filter options
 * so users never see options that would return zero results.
 */
import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

interface DatasetFacets {
  /** Industry IDs present in the page's dataset */
  availableIndustryIds: Set<string>;
  /** Category IDs present in the page's dataset */
  availableCategoryIds: Set<string>;
}

interface DatasetFacetsContextValue extends DatasetFacets {
  setFacets: (industryIds: Set<string>, categoryIds: Set<string>) => void;
}

const DEFAULT: DatasetFacetsContextValue = {
  availableIndustryIds: new Set(),
  availableCategoryIds: new Set(),
  setFacets: () => {},
};

const Ctx = createContext<DatasetFacetsContextValue>(DEFAULT);

export const DatasetFacetsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [facets, setFacetsState] = useState<DatasetFacets>({
    availableIndustryIds: new Set(),
    availableCategoryIds: new Set(),
  });

  const setFacets = useCallback((industryIds: Set<string>, categoryIds: Set<string>) => {
    setFacetsState({ availableIndustryIds: industryIds, availableCategoryIds: categoryIds });
  }, []);

  const value = useMemo(
    () => ({ ...facets, setFacets }),
    [facets, setFacets],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useDatasetFacets = () => useContext(Ctx);

/**
 * Derive available facet IDs from a dataset of items with industry_id/category_id.
 */
export function deriveAvailableFacetIds(items: Array<{ industry_id?: string | null; category_id?: string | null }>): {
  industryIds: Set<string>;
  categoryIds: Set<string>;
} {
  const industryIds = new Set<string>();
  const categoryIds = new Set<string>();
  for (const item of items) {
    if (item.industry_id) industryIds.add(item.industry_id);
    if (item.category_id) categoryIds.add(item.category_id);
  }
  return { industryIds, categoryIds };
}
